import {
  FLEET_DOCUMENT_ALLOWED_MIME,
  FLEET_FILE_MAX_BYTES,
  FleetDocumentKind,
} from '@gb-ticket/shared-types';
import { type FormEvent, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import {
  FLEET_DOCUMENT_KIND_LABELS,
  formatDeDateTime,
} from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import { FleetDocumentDeleteButton } from '@/products/fleet/features/fleet-document-delete-button';
import {
  fleetDocumentDownloadHref,
  useFleetVehicleDocuments,
  useUploadFleetDocument,
} from '@/products/fleet/features/use-fleet';

const uploadKinds = [
  FleetDocumentKind.REGISTRATION,
  FleetDocumentKind.INSPECTION_REPORT,
  FleetDocumentKind.LEASING,
  FleetDocumentKind.INSURANCE,
  FleetDocumentKind.INVOICE,
  FleetDocumentKind.OTHER,
] as const;

const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.docx'];

function uploadFileError(file: File): string | null {
  if (file.size <= 0) {
    return 'Die Datei ist leer.';
  }
  if (file.size > FLEET_FILE_MAX_BYTES) {
    return 'Einzeldatei höchstens 10 MB.';
  }
  const mime = file.type.toLowerCase();
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  const mimeAllowed =
    mime.length > 0 && (FLEET_DOCUMENT_ALLOWED_MIME as readonly string[]).includes(mime);
  const extensionAllowed = ALLOWED_EXTENSIONS.includes(extension);
  if (!mimeAllowed && !extensionAllowed) {
    return 'Dieser Dateityp ist nicht erlaubt. Erlaubt sind PDF, JPEG, PNG, WebP und DOCX.';
  }
  return null;
}

export function FleetVehicleDocumentsTab({
  vehicleId,
  canUpload,
}: {
  vehicleId: string;
  canUpload: boolean;
}) {
  const documents = useFleetVehicleDocuments(vehicleId);
  const upload = useUploadFleetDocument(vehicleId);
  const fileInput = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<string>(FleetDocumentKind.REGISTRATION);
  const [file, setFile] = useState<File | null>(null);
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const items = documents.data?.items ?? [];

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!file) {
      setFeedback({ tone: 'error', text: 'Bitte eine Datei wählen.' });
      return;
    }
    const invalid = uploadFileError(file);
    if (invalid) {
      setFeedback({ tone: 'error', text: invalid });
      toast.error(invalid);
      return;
    }
    setFeedback(null);
    try {
      await upload.mutateAsync({ kind, file });
      setFile(null);
      if (fileInput.current) {
        fileInput.current.value = '';
      }
      setFeedback({ tone: 'ok', text: 'Dokument hochgeladen.' });
      toast.success('Dokument hochgeladen.');
    } catch (error: unknown) {
      const message = error instanceof ApiRequestError ? error.message : 'Upload fehlgeschlagen.';
      setFeedback({ tone: 'error', text: message });
      toast.error(message);
    }
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {items.length === 1 ? '1 Dokument' : `${items.length} Dokumente`}
      </p>
      {items.length > 0 ? (
        <ul className={`${fleetTableFrameClassName} divide-y text-sm`}>
          {items.map((doc) => (
            <li key={doc.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <div>
                <p className="font-medium">{doc.fileName}</p>
                <p className="text-muted-foreground">
                  {FLEET_DOCUMENT_KIND_LABELS[doc.kind]} · {formatDeDateTime(doc.createdAt)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href={fleetDocumentDownloadHref(doc.id)}
                  className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Herunterladen
                </a>
                {canUpload ? (
                  <FleetDocumentDeleteButton
                    documentId={doc.id}
                    fileName={doc.fileName}
                    subject="an diesem Fahrzeug"
                  />
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Noch keine Dokumente.</p>
      )}

      {canUpload ? (
        <form
          className={`${fleetPanelClassName} space-y-3`}
          aria-busy={upload.isPending}
          onSubmit={(event) => void onSubmit(event)}
        >
          <h3 className="text-base font-semibold">Dokument hochladen</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="doc-kind">Art</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger id="doc-kind" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {uploadKinds.map((value) => (
                    <SelectItem key={value} value={value}>
                      {FLEET_DOCUMENT_KIND_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="doc-file">Datei</Label>
              <Input
                ref={fileInput}
                id="doc-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,application/pdf,image/*"
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                  setFeedback(null);
                }}
                required
              />
            </div>
          </div>
          {upload.isPending ? (
            <p role="status" className="text-sm text-muted-foreground">
              Wird hochgeladen …
            </p>
          ) : null}
          {feedback ? (
            <p
              className={
                feedback.tone === 'error'
                  ? 'text-sm text-destructive'
                  : 'text-sm text-muted-foreground'
              }
            >
              {feedback.text}
            </p>
          ) : null}
          <Button type="submit" className="min-h-11" disabled={upload.isPending}>
            {upload.isPending ? 'Wird hochgeladen …' : 'Hochladen'}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
